<template>
  <div class="form-selector-validated">
    <label :for="selectName">{{selectLabel}}</label>
    <div class="select-wrapper">
      <select
        :class="selectComputedClassName"
        :name="selectName"
        :id="selectName"
        @change="selectAndVerifyValue"
        v-model="verifiedSelectedOption"
        @blur="onBlur"
        @focus="onFocus"
      >
        <option :key="option" v-for="option in options" :value="option">{{ option }}</option>
      </select>
      <span v-if="showSuccessIcon" class="validation-icon success-icon">✓</span>
      <span v-if="showErrorIcon" class="validation-icon error-icon">✕</span>
      <svg class="select-arrow" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="m6 9 6 6 6-6"></path>
      </svg>
    </div>
    <span v-if="showError" class="error-message">{{errorText}}</span>
    <span v-if="showSuccess" class="success-message">✓ Câmp valid</span>
  </div>
</template>

<script>
export default {
  props: {
    options: Array,
    selectValue: Function,
    selectName: String,
    selectLabel: String,
    isValid: Boolean,
    errorText: String,
    selectedOption: String,
    selectorSize: String,
    validateOnChange: {
      type: Boolean,
      default: true
    },
    forceShowErrors: {
      type: Boolean,
      default: false // Force show errors (e.g., on form submit)
    }
  },
  data() {
    return {
      verifiedSelectedOption:
        (!this.selectedOption || this.selectedOption.length === 0 || this.selectedOption === "") 
          ? (this.options && this.options.length > 0 ? this.options[0] : "") 
          : this.selectedOption,
      hasInteracted: false,
      isFocused: false
    };
  },
  computed: {
    selectComputedClassName: function() {
      let classes = [];
      
      if (this.showError) {
        classes.push("error");
      } else if (this.showSuccess && this.hasInteracted) {
        classes.push("success");
      }
      
      if (this.isFocused) {
        classes.push("focused");
      }
      
      return classes.join(" ");
    },
    showError() {
      // Show error if forced (on submit) or if user has interacted
      if (this.forceShowErrors || this.hasInteracted) {
        return !this.isValid && this.errorText;
      }
      return false;
    },
    showSuccess() {
      // Only show success if user has interacted (not on forced errors)
      if (!this.hasInteracted) return false;
      return this.isValid && this.verifiedSelectedOption && 
             this.verifiedSelectedOption !== this.options[0] && 
             this.verifiedSelectedOption !== "Alege județul" && 
             this.verifiedSelectedOption !== "Alege orașul";
    },
    showErrorIcon() {
      return this.showError && (this.forceShowErrors || this.hasInteracted);
    },
    showSuccessIcon() {
      return this.showSuccess && this.hasInteracted && !this.isFocused;
    }
  },
  methods: {
    selectAndVerifyValue: function(event) {
      const value = event.target.value;
      if (value && this.options.indexOf(value) > 0) {
        this.selectValue(value);
      } else {
        this.selectValue("");
      }
      
      this.hasInteracted = true;
    },
    onBlur() {
      this.isFocused = false;
      this.hasInteracted = true;
    },
    onFocus() {
      this.isFocused = true;
    }
  }
};
</script>

<style lang="scss" scoped>
label {
  display: block;
  margin-bottom: 6px;
  font-size: 14px;
  font-weight: 500;
  color: #64748b;
  line-height: 1.4;
}

.select-wrapper {
  position: relative;
  width: 100%;
}

select {
  display: block;
  padding: 14px 60px 14px 16px; /* Extra padding for icons */
  font-size: 16px;
  width: 100%;
  border: 2px solid #e2e8f0;
  border-radius: 6px;
  background-color: #f8fafc;
  cursor: pointer;
  transition: all 0.3s ease;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  color: #1a1a1a;
  font-weight: 500;

  &:hover {
    border-color: #cbd5e1;
    background-color: #f0f4f8;
  }

  &:focus {
    outline: none;
    border-color: #bd1f23;
    box-shadow: 0 0 0 3px rgba(189, 31, 35, 0.1);
    background-color: #fff;
  }

  &.error {
    border-color: #e53e3e;
    background-color: #fff;
    animation: shake 0.3s ease;
    
    &:focus {
      border-color: #e53e3e;
      box-shadow: 0 0 0 3px rgba(229, 62, 62, 0.1);
    }
  }

  &.success {
    border-color: #10b981;
    background-color: #fff;
    
    &:focus {
      border-color: #10b981;
      box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
    }
  }

  &.focused {
    border-color: #bd1f23;
  }
}

.select-arrow {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  color: #64748b;
  width: 20px;
  height: 20px;
  z-index: 1;
}

.validation-icon {
  position: absolute;
  right: 40px;
  top: 50%;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
  pointer-events: none;
  z-index: 2;
  animation: fadeIn 0.3s ease;
}

.success-icon {
  background-color: #10b981;
  color: #fff;
}

.error-icon {
  background-color: #e53e3e;
  color: #fff;
}

.error-message {
  display: block;
  margin: 6px 0 0 0;
  color: #e53e3e;
  font-size: 14px;
  animation: slideDown 0.3s ease;
}

.success-message {
  display: block;
  margin: 6px 0 0 0;
  color: #10b981;
  font-size: 14px;
  font-weight: 500;
  animation: slideDown 0.3s ease;
}

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-5px); }
  75% { transform: translateX(5px); }
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-50%) scale(0.8);
  }
  to {
    opacity: 1;
    transform: translateY(-50%) scale(1);
  }
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-5px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>

